import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import FigureNodeView from './FigureNodeView';

export interface FigureOptions {
  HTMLAttributes: Record<string, unknown>;
  /** ID of the project that owns the figures — required for the NodeView fetch. */
  projectId: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    figure: {
      insertFigure: (attrs: {
        figureId: string;
        alt?: string;
        caption?: string;
        label?: string;
        /** @deprecated src is no longer stored; the NodeView fetches the image
         *  via the authenticated API client. Kept for API compatibility. */
        src?: string;
      }) => ReturnType;
    };
  }
}

export const FigureExtension = Node.create<FigureOptions>({
  name: 'figure',

  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    return { HTMLAttributes: {}, projectId: '' };
  },

  addAttributes() {
    return {
      // src is kept in-memory only (for backward compat with callers that still
      // pass it); it is intentionally NOT serialised to HTML so that no token
      // or stale URL is ever persisted to the database.
      src: { default: null, rendered: false },
      alt: { default: '' },
      figureId: {
        default: null,
        parseHTML: el => el.getAttribute('data-figure-id'),
      },
      caption: { default: '' },
      label: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'figure[data-figure-id]' }];
  },

  /**
   * Serialises the node to HTML for persistence (editor.getHTML()).
   * Only stable, credential-free attributes are written.
   * The live image is resolved at render time by FigureNodeView.
   */
  renderHTML({ HTMLAttributes }) {
    const { alt, figureId, caption, label } = HTMLAttributes;
    const captionText = label || caption || '';
    return [
      'figure',
      mergeAttributes(this.options.HTMLAttributes, {
        'data-figure-id': figureId,
        class: 'rda-figure',
      }),
      [
        'img',
        {
          alt: alt || captionText,
          class: 'rda-figure__img',
        },
      ],
      ['figcaption', { class: 'rda-figure__caption' }, captionText],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FigureNodeView);
  },

  addCommands() {
    return {
      insertFigure: attrs => ({ commands }) => {
        return commands.insertContent({ type: this.name, attrs });
      },
    };
  },
});
